import { PwaProvider } from '@/features/pwa/PwaProvider';
import { PwaRuntime } from '@/features/pwa/PwaRuntime';
import { usePreferencesStore } from '@/stores/preferences.store';
import { ActionCelebration } from './ActionCelebration';
import { ToastViewport } from '@/components/ui/ToastViewport';
import { type ReactNode, useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/query-client';
import { attachAudioUnlockListeners, stopSounds } from '@/lib/sfx';
import { InstallPromptBanner } from '@/components/layout/InstallPromptBanner';
import { AppMotionConfig } from './AppMotionConfig';

/**
 * Theme is applied by the preferences store's own subscription (stores/preferences.store.ts) —
 * this provider's job is just the cross-cutting app-wide bits: React Query, Framer Motion's
 * reduced-motion switch, and arming the sound unlock listener. Never fetches `/me`, so genuinely
 * session checks remain owned by route components.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  useEffect(() => {
    attachAudioUnlockListeners(() => usePreferencesStore.getState().sound);
    const unsubscribe = usePreferencesStore.subscribe((state) => {
      if (!state.sound || state.soundVolume === 0) stopSounds();
    });
    const hide = () => {
      document.documentElement.toggleAttribute(
        'data-effects-hidden',
        document.visibilityState === 'hidden',
      );
      if (document.visibilityState === 'hidden') stopSounds();
    };
    hide();
    document.addEventListener('visibilitychange', hide);
    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', hide);
      document.documentElement.removeAttribute('data-effects-hidden');
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <PwaProvider>
        <AppMotionConfig>
          <ActionCelebration />
          {children}
          <InstallPromptBanner />
          <PwaRuntime />
          <ToastViewport />
        </AppMotionConfig>
      </PwaProvider>
    </QueryClientProvider>
  );
}
